/**
 * Formulaire d'onboarding — au 1er lancement de la PWA.
 * L'agent saisit nom, prénoms, téléphone (facultatif) + code d'accès.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, UserPlus, AlertCircle, KeyRound, Loader2 } from 'lucide-react';
import { enregistrer, getOrCreateDeviceId } from '../services/sessionApi';

export default function Onboarding() {
  const navigate = useNavigate();
  const [nom, setNom] = useState('');
  const [prenoms, setPrenoms] = useState('');
  const [telephone, setTelephone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (nom.trim().length < 2 || prenoms.trim().length < 2) {
      setError('Nom et prénoms requis (min 2 caractères).');
      return;
    }
    if (code.trim().length < 3) {
      setError("Code d'accès requis.");
      return;
    }
    setLoading(true);
    try {
      await enregistrer({
        nom: nom.trim(),
        prenoms: prenoms.trim(),
        telephone: telephone.trim(),
        code_acces: code.trim(),
      });
      navigate('/verif', { replace: true });
    } catch (err: any) {
      if (err.code === 'code_invalide') {
        setError("Code d'accès incorrect. Contactez la caisse.");
      } else if (err.status === 429) {
        setError('Trop de tentatives. Patientez quelques minutes.');
      } else {
        setError(err.message || 'Erreur, réessayez.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-sm w-full">
        <div className="text-center mb-6">
          <div className="inline-block bg-white rounded-2xl p-3 shadow-xl mb-3">
            <img src="/verif-icon-192.png" alt="2i Recouvrement"
                 className="w-16 h-16 rounded-lg" />
          </div>
          <div className="text-white text-2xl font-bold">2i Recouvrement</div>
          <div className="text-blue-200 text-sm mt-1">
            Premier lancement — enregistrement de l'appareil
          </div>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl shadow-2xl p-5 space-y-3"
        >
          <div className="flex items-center gap-2 text-blue-900 font-semibold pb-2 border-b border-gray-100">
            <UserPlus size={18} />
            <span>Vos identifiants</span>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase mb-1 block">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
              placeholder="MABAYA"
              autoCapitalize="characters"
              autoComplete="family-name"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase mb-1 block">
              Prénoms <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={prenoms}
              onChange={(e) => setPrenoms(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Graciace Divine"
              autoComplete="given-name"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase mb-1 block">
              Téléphone <span className="text-gray-400 normal-case">(facultatif)</span>
            </label>
            <input
              type="tel"
              value={telephone}
              onChange={(e) => setTelephone(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="+242 06 XXX XX XX"
              autoComplete="tel"
            />
          </div>

          <div className="pt-2 border-t border-gray-100">
            <label className="text-xs font-semibold text-gray-600 uppercase mb-1 flex items-center gap-1">
              <KeyRound size={12} /> Code d'accès (donné par la caisse) <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-lg tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="••••"
              inputMode="numeric"
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Enregistrement…</>
                     : <><ShieldCheck size={16} /> S'enregistrer et continuer</>}
          </button>

          <div className="text-[11px] text-gray-500 text-center pt-2">
            Institut International 2i · Usage restreint aux agents habilités
          </div>
        </form>
      </div>
    </div>
  );
}
